// Helper to convert minutes to HH:mm format
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours.toString().padStart(2, '0') + ':' + mins.toString().padStart(2, '0');
};

// Helper to convert HH:mm to minutes
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Format time for display (e.g., "9:00 AM")
export const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  // Use current date to avoid hardcoded year issues
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format date for display (e.g., "Monday, June 19")
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};

// Format date in short form (e.g., "Mon, Jun 19")
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

// Check if a time slot is available for booking
export const checkTimeSlotAvailability = async (property, date, time, existingBookings) => {
  // Fix timezone issue by creating date in local timezone
  const [year, month, day] = date.split('-').map(Number);
  // Create date at noon to avoid any timezone edge cases
  const dateObject = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = dateObject.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  
  // Debug timezone conversion
  console.log('🔍 Timezone debugging:');
  console.log('  📅 Input date:', date);
  console.log('  📅 Date object (local):', dateObject.toLocaleString());
  console.log('  📅 Day of week:', dayOfWeek);
  console.log('  📅 Local timezone offset:', -dateObject.getTimezoneOffset()/60, 'hours');
  
  // Debug logging to help troubleshoot availability issues
  console.log(`🔍 Checking availability for date: ${date}, day: ${dayOfWeek}, time: ${time}`);
  console.log('📋 Property object:', property);
  
  // Add null check for property
  if (!property) {
    console.log('❌ Property is null/undefined');
    return {
      available: false,
      reason: 'Property data not available'
    };
  }
  
  console.log('📋 Property custom_availability:', property.custom_availability);
  console.log('📋 Property availability_type:', property.availability_type);
  
  let startTime, endTime;
  
  // Parse custom_availability if it's a string (from backend JSON)
  let customAvailability = property.custom_availability;
  if (typeof customAvailability === 'string') {
    try {
      customAvailability = JSON.parse(customAvailability);
    } catch (e) {
      console.error('Failed to parse custom_availability JSON:', e);
      customAvailability = null;
    }
  }

  // Check if property has custom availability (check both availability_type and presence of custom_availability)
  if (customAvailability && (property.availability_type === 'custom' || Object.keys(customAvailability).length > 0)) {
    const daySchedule = customAvailability[dayOfWeek];
    console.log(`📅 Custom day schedule for ${dayOfWeek}:`, daySchedule);
    
    if (!daySchedule) {
      console.log(`❌ No custom schedule found for ${dayOfWeek}`);
      return {
        available: false,
        reason: 'Schedule not available for this day'
      };
    }
    
    // Check if day is enabled
    if (!daySchedule.enabled) {
      console.log(`❌ Day ${dayOfWeek} is disabled in custom schedule`);
      return {
        available: false,
        reason: 'Showings are not available on this day'
      };
    }
    
    startTime = timeToMinutes(daySchedule.start);
    endTime = timeToMinutes(daySchedule.end);
    console.log(`✅ Day ${dayOfWeek} is enabled (${daySchedule.start} - ${daySchedule.end})`);
  } else {
    // No custom availability configured
    console.log(`⚠️ No custom availability for time slot check. Property availability_type: ${property.availability_type}`);
    console.log(`⚠️ Custom availability object:`, customAvailability);
    return {
      available: false,
      reason: 'Property has not configured custom availability'
    };
  }
  
  const requestedTime = timeToMinutes(time);
  
  if (requestedTime < startTime || requestedTime >= endTime) {
    return {
      available: false,
      reason: 'Showings are only available between ' + formatTime(minutesToTime(startTime)) + ' and ' + formatTime(minutesToTime(endTime)) + ' on ' + dayOfWeek
    };
  }
  
  // Check for conflicts with existing bookings considering buffer time
  const bufferTime = property.buffer_time_minutes || 0;
  const showingDuration = 30; // 30-minute showings
  
  const requestedStartBuffer = requestedTime - bufferTime;
  const requestedEndBuffer = requestedTime + showingDuration + bufferTime;
  
  // Log existing bookings for debugging
  console.log('📋 Checking conflicts with existing bookings:', existingBookings);

  const conflicts = existingBookings.filter(booking => {
    // Handle different date field names (showing_date vs showingDate)
    const bookingDate = booking.showing_date || booking.showingDate;
    const bookingTimeStr = booking.showing_time || booking.showingTime;
    
    console.log('🔍 Comparing booking:', {
      date: bookingDate,
      time: bookingTimeStr,
      status: booking.status,
      requestDate: date,
      requestTime: time
    });
    
    // Only check bookings on the same date that aren't cancelled
    if (bookingDate !== date || booking.status === 'cancelled') {
      console.log('✅ No conflict - different date or cancelled');
      return false;
    }
    
    const bookingTime = timeToMinutes(bookingTimeStr);
    const bookingStartBuffer = bookingTime - bufferTime;
    const bookingEndBuffer = bookingTime + showingDuration + bufferTime;
    
    console.log('⏰ Time ranges:', {
      requested: `${minutesToTime(requestedStartBuffer)}-${minutesToTime(requestedEndBuffer)}`,
      booking: `${minutesToTime(bookingStartBuffer)}-${minutesToTime(bookingEndBuffer)}`
    });
    
    // Check if time ranges overlap
    const hasConflict = requestedStartBuffer < bookingEndBuffer && bookingStartBuffer < requestedEndBuffer;
    if (hasConflict) {
      console.log('❌ Conflict detected!');
    } else {
      console.log('✅ No time conflict');
    }
    return hasConflict;
  });
  
  if (conflicts.length > 0) {
    return {
      available: false,
      reason: 'Time slot conflicts with an existing showing',
      conflicts: conflicts.map(booking => ({
        time: booking.showing_time,
        buffer: bufferTime
      }))
    };
  }
  
  return {
    available: true
  };
};

// Global business hours (9am-5pm PST)
const GLOBAL_BUSINESS_HOURS = {
  start: '09:00',
  end: '17:00'
};

// Get available time slots for a date
export const getAvailableTimeSlots = async (property, date, existingBookings) => {
  // Calculate 24-hour notice cutoff
  const now = new Date();
  const selectedDate = new Date(date);
  
  // Convert to timestamps for accurate 24-hour comparison
  const nowTimestamp = now.getTime();
  
  // Initialize min time to global business hours start
  let minTime = GLOBAL_BUSINESS_HOURS.start;
  
  // Calculate time difference in hours
  const hoursDiff = (selectedDate.getTime() - nowTimestamp) / (1000 * 60 * 60);
  
  // If within 24 hours, calculate exact cutoff time
  if (hoursDiff < 24) {
    // Add 24 hours to current time
    const cutoffTime = new Date(nowTimestamp + (24 * 60 * 60 * 1000));
    const cutoffHours = cutoffTime.getHours();
    const cutoffMinutes = cutoffTime.getMinutes();
    
    // Round up to next 30-minute slot
    const roundedMinutes = Math.ceil(cutoffMinutes / 30) * 30;
    const adjustedHours = cutoffHours + Math.floor(roundedMinutes / 60);
    const adjustedMinutes = roundedMinutes % 60;
    
    minTime = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
    
    // If cutoff time is after business hours, no slots available
    if (timeToMinutes(minTime) > timeToMinutes(GLOBAL_BUSINESS_HOURS.end)) {
      console.log('❌ Cutoff time is after business hours');
      return [];
    }
  }

  // Fix timezone issue by creating date in local timezone
  const [year, month, day] = date.split('-').map(Number);
  // Create date at noon to avoid any timezone edge cases
  const dateObject = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = dateObject.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  
  // Debug timezone conversion
  console.log('🔍 Timezone debugging:');
  console.log('  📅 Input date:', date);
  console.log('  📅 Date object (local):', dateObject.toLocaleString());
  console.log('  📅 Day of week:', dayOfWeek);
  console.log('  📅 Local timezone offset:', -dateObject.getTimezoneOffset()/60, 'hours');
  
  console.log(`🕐 Getting time slots for ${date} (${dayOfWeek})`);
  console.log('🔍 Date debugging:');
  console.log('  📅 Input date string:', date);
  console.log('  📅 Date object:', dateObject);
  console.log('  📅 Day of week number (0=Sunday):', dateObject.getDay());
  console.log('  📅 Day name (full):', dateObject.toLocaleDateString('en-US', { weekday: 'long' }));
  console.log('  📅 Day name (lowercase):', dayOfWeek);
  console.log('🏢 Property object structure:', property);
  
  // Add null check for property
  if (!property) {
    console.log('❌ Property is null/undefined in getAvailableTimeSlots');
    return [];
  }
  
  console.log('⚙️ Property availability_type:', property.availability_type);
  console.log('📅 Property custom_availability:', property.custom_availability);
  console.log('🔍 Custom availability keys:', property.custom_availability ? Object.keys(property.custom_availability) : 'none');
  console.log('🔍 Looking for day key:', dayOfWeek);
  console.log('🔍 Day schedule raw:', property.custom_availability?.[dayOfWeek]);
  
  let daySchedule = null;
  let startTime, endTime;
  
  // Parse custom_availability if it's a string (from backend JSON)
  let customAvailability = property.custom_availability;
  if (typeof customAvailability === 'string') {
    try {
      customAvailability = JSON.parse(customAvailability);
    } catch (e) {
      console.error('Failed to parse custom_availability JSON:', e);
      customAvailability = null;
    }
  }

  // First apply global business hours
  startTime = Math.max(timeToMinutes(GLOBAL_BUSINESS_HOURS.start), timeToMinutes(minTime));
  endTime = timeToMinutes(GLOBAL_BUSINESS_HOURS.end);

  // Then check for custom availability logic
  console.log('🔧 Checking custom availability for debugging...');
  if (customAvailability) {
    daySchedule = customAvailability[dayOfWeek];
    console.log('📅 Custom day schedule:', daySchedule);
    console.log('🔍 Day schedule exists?', !!daySchedule);
    console.log('🔍 Day schedule enabled?', daySchedule?.enabled);
    console.log('🔍 Day schedule enabled type:', typeof daySchedule?.enabled);
    console.log('🔍 Raw enabled value:', daySchedule?.enabled);
    
    // If day schedule not found or disabled, return no slots
    if (!daySchedule) {
      console.log(`❌ No day schedule found for ${dayOfWeek}`);
      return [];
    }
    
    if (!daySchedule.enabled) {
      console.log(`❌ Day ${dayOfWeek} is disabled - enabled value:`, daySchedule.enabled);
      return [];
    }
    
    // Merge global and custom hours - take the most restrictive range
    startTime = Math.max(startTime, timeToMinutes(daySchedule.start));
    endTime = Math.min(endTime, timeToMinutes(daySchedule.end));
    console.log(`✅ Using merged schedule for ${dayOfWeek}: ${minutesToTime(startTime)} - ${minutesToTime(endTime)}`);
  } else {
    // Fallback: Check if we should show default schedule or no schedule
    console.log(`⚠️ No custom availability configured. Property availability_type: ${property.availability_type}`);
    console.log(`⚠️ Custom availability object:`, customAvailability);
    
    // For now, if no custom availability is set, don't show any slots to avoid confusion
    // This forces users to configure their availability properly
    console.log(`❌ No custom schedule configured - showing no slots for ${dayOfWeek}`);
    return [];
  }
  
  console.log(`✅ Generating slots for ${dayOfWeek} from ${minutesToTime(startTime)} to ${minutesToTime(endTime)}`);
  
  const slots = [];
  
  // Convert minTime to minutes for comparison
  const minTimeMinutes = timeToMinutes(minTime);
  console.log('⏰ Min time in minutes:', minTimeMinutes);
  
  // Generate 30-minute slots and check availability against existing bookings
  for (let time = startTime; time < endTime; time += 30) {
    // Skip slots before minimum time
    if (time < minTimeMinutes) {
      console.log('⏰ Skipping slot before minimum time:', minutesToTime(time));
      continue;
    }
    
    const timeString = minutesToTime(time);
    console.log('⏰ Checking slot:', timeString);
    
    // Check this time slot against existing bookings
    const availability = await checkTimeSlotAvailability(property, date, timeString, existingBookings || []);
    
    slots.push({
      time: timeString,
      available: availability.available,
      reason: typeof availability.reason === 'object' ? JSON.stringify(availability.reason) : String(availability.reason),
      conflicts: availability.conflicts
    });
  }
  
  console.log(`📊 Generated ${slots.length} time slots for ${dayOfWeek}:`, slots.map(s => `${s.time}${s.available ? '✅' : '❌'}`));
  
  return slots;
};

// Generate comprehensive availability report
export const generateAvailabilityReport = async (property, selectedDate, selectedTime, existingBookings) => {
  const availability = await checkTimeSlotAvailability(
    property,
    selectedDate,
    selectedTime,
    existingBookings
  );

  if (availability.available) {
    return {
      status: 'available',
      message: 'Time slot is available',
      issues: []
    };
  }

  // Get next available slots if this one isn't available
  const nextSlots = await getAvailableTimeSlots(property, selectedDate, existingBookings);
  const availableSlots = nextSlots.filter(slot => slot.available).slice(0, 3);

  return {
    status: 'unavailable',
    message: availability.reason,
    issues: [{
      type: 'error',
      message: availability.reason
    }],
    alternatives: availableSlots.length > 0 ? {
      message: 'Alternative times available today:',
      slots: availableSlots.map(slot => ({
        time: slot.time,
        label: formatTime(slot.time)
      }))
    } : undefined
  };
};

// Get next available slots over multiple days
export const getNextAvailableSlots = async (property, startDate, days = 7) => {
  const slots = [];
  const date = new Date(startDate);
  
  for (let i = 0; i < days; i++) {
    const dateString = date.toISOString().split('T')[0];
    const daySlots = await getAvailableTimeSlots(property, dateString, property.existingBookings || []);
    
    if (daySlots.length > 0) {
      slots.push({
        date: dateString,
        formattedDate: formatDate(dateString),
        slots: daySlots
      });
    }
    
    date.setDate(date.getDate() + 1);
  }
  
  return slots;
};